/**
 * The phone that stays signed in (the mobile app): added right after a sign-in in the app, behind the
 * phone's own Face ID, fingerprint or PIN; reopened with its key, which comes back replaced each time.
 * Sign out on the phone forgets it, here and on the server.
 */
import { authApi } from '../api'
import { appBridge, type Prompt } from './bridge'

/** Who signed in, as /auth/login answers (the key's session answers the same) */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type SignedIn = any
const errorCode = (e: unknown) => (e as { response?: { status?: number; data?: { code?: string } } })?.response

/** After a sign-in in the app: this phone stays signed in, if it can be unlocked and the person agrees (once, however often asked) */
let adding: Promise<void> | null = null
export function addPhoneAfterSignIn(prompt: Prompt): Promise<void> {
  return (adding ??= addOnce(prompt).finally(() => { adding = null }))
}

async function addOnce(prompt: Prompt): Promise<void> {
  const app = appBridge()
  if (!app?.features.phone || await app.storedPhone() || !await app.canUnlock()) return
  if (!await app.confirm(prompt)) return
  let added: { id: string; key: string } | null = null
  try {
    const d = await app.device()
    added = (await authApi.addPhone({ name: d.name, model: d.model, platform: d.platform })).data.data as { id: string; key: string }
    await app.savePhone(added, prompt)
  } catch {
    // the company may not let phones stay signed in, or the phone could not keep the key: the browser's rules then
    if (added) await authApi.removePhone(added.id).catch(() => undefined)
  }
}

/**
 * The stored phone opens a session after Face ID; null when there is none, it was refused, or it was removed.
 * One unlock at a time: two with the same key would look like a copied key, and the phone would be removed.
 */
let unlocking: Promise<SignedIn | null> | null = null
export function unlockWithPhone(prompt: Prompt): Promise<SignedIn | null> {
  return (unlocking ??= unlockOnce(prompt).finally(() => { unlocking = null }))
}

async function unlockOnce(prompt: Prompt): Promise<SignedIn | null> {
  const app = appBridge()
  // the key comes out of the phone's storage only through its own Face ID / fingerprint prompt
  const phone = app?.features.phone && await app.storedPhone() ? await app.readPhone(prompt) : null
  if (!app || !phone) return null
  try {
    const d = (await authApi.unlockPhone(phone.id, phone.key)).data.data
    // the new key first: the old one, used again, would remove this phone
    await app.savePhone({ id: phone.id, key: d.key }, prompt)
    return d
  } catch (e) {
    const r = errorCode(e)
    if (r?.status === 401 && r.data?.code === 'app_device_removed') await app.savePhone(null)
    else throw e
    return null
  }
}

/** Is a phone stored here (the sign-in page then offers Unlock) */
export async function hasPhone(): Promise<boolean> {
  const app = appBridge()
  return !!(app?.features.phone && await app.storedPhone())
}

/** Sign out on the phone: the phone forgotten on the server and here (no Face ID needed for that) */
export async function forgetPhone(): Promise<void> {
  const app = appBridge()
  // the push token goes too: Firebase hands out a new one, so no server left behind can push to this phone
  await app?.deletePushToken?.()
  const id = app?.features.phone ? await app.storedPhone() : null
  if (!app || !id) return
  await authApi.removePhone(id).catch(() => undefined)
  await app.savePhone(null)
}
